import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Toast visual variants — mirror Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, Toast component set. Reference nodes:
 *   - 1878:26565 – Compact / Default / icon+title+desc / dismissable / action
 *   - 1878:26561 – Compact / Critical  (filled-red action button)
 *   - 1878:26643 – Compact / Success   (filled-green action button)
 *   - 1878:26563 – Regular / Default   (medium-size geometry + typography)
 *   - 1901:3838  – Compact / Default / title-only
 *
 * Axes:
 *   variant: default | success | critical | conversational   (Figma "Type")
 *   size:    sm (Compact 320) | md (Regular 400)
 *
 * Anatomy (row): [leading icon?] — [title + description? — flex-1] —
 *   [trailing badge?] — [action Button?] — [dismiss X?]. Container is a
 *   1px-bordered translucent card with a soft drop shadow (`shadow-toast`,
 *   web-only — native shadow comes from `Toast.shadows.ts` because NativeWind
 *   v4 can't compile a CSS box-shadow to RN style props).
 *
 * The `conversational` variant is a Professor-Ka speech toast and breaks the
 * anatomy above: it is single-line CONTENT only (no title/description split,
 * no leading status icon), keeps only the optional trailing badge, and owns its
 * own geometry (see the CONVERSATIONAL_* constants) instead of the sm/md sizes.
 * Figma nodes: 5226:5313 (in-chat bubble — squared bottom-right corner) and
 * 5226:5493 (system fallback — symmetric, with Ka on the left).
 *
 * Surface fills are translucent brand-token fills in Figma. Each is a plain
 * palette shade at an alpha, expressed via the Tailwind opacity modifier so
 * the CSS-var palette AUTO-INVERTS in dark mode (see the shared preset's
 * `palette()` → `rgba(var(--…-rgb), <alpha-value>)`). This matches the Figma
 * light AND dark nodes exactly — the dark nodes use the identical token, only
 * the resolved var differs:
 *   default  → `secondary-100/70`  (Figma `brand/secondary/130` = #f5f5f5b2 → dark #171717b2)
 *   critical → `red-400/20`        (Figma `color/red/320`       = #ff646733 → dark #e7000b33)
 *   success  → `green-300/30`      (Figma `color/green/270`     = #7bf1a84d → dark #0082364d)
 * Borders are the plain `{secondary,red,green}-200` tokens (auto-inverting).
 * No `dark:` overrides — the design system's palette inversion does all the
 * dark-mode work (verified against Figma dark nodes 2787:38009/38023/38044).
 *
 * The action button is the shared {@link Button} (composed in Toast.tsx), so
 * the bevel / inset-highlight / pressed states are inherited, not re-built:
 *   default → Button `secondary` ; critical → Button `alert` ;
 *   success → Button `success`. Sizes: sm → Button `s` (36px), md → `m` (40px).
 *
 * Dark mode: handled entirely by the CSS-var palette auto-inversion — the
 * Figma dark nodes (2787:37155) use the SAME token classes as light, so no
 * `dark:` overrides are needed. Every colour below is a plain palette token.
 *
 * Focus (Figma "State=Focus"): a 1px web focus ring per variant. Figma renders
 * focus as an outer wrapper with a 1px border in the variant's 800 shade
 * (default uses secondary-600); we mirror it with `ring-1` (no layout shift).
 *   default → secondary-600 ; critical → red-800 ; success → green-800.
 * `web:` keeps `focus-visible:`/`ring-*` off native (NativeWind would otherwise
 * apply the ring shadow unconditionally).
 */
/**
 * The per-variant translucent SURFACE FILL, hoisted out of the CVA below so the
 * native glass layer can re-apply the exact same class.
 *
 * It has to be re-applied rather than read off the card, because Android gets no
 * blur (see `glass.ts`) and its fallback paints an OPAQUE base inside the card:
 * that base covers the card's own fill, so the tint goes back on top of it. One
 * map, two consumers — a second hardcoded copy in the native layer is how a
 * variant's toast ends up a different colour per platform.
 */
export const TOAST_FILL_CLASS = {
  default: 'bg-surface-overlay/70',
  critical: 'bg-red-400/20',
  success: 'bg-green-300/30',
  conversational: 'bg-surface-overlay/70',
} as const;

export const toastVariants = cva(
  // `native:self-center`: the card declares `w-full max-w-[…]` (grow, capped) —
  // on web the toast engine (sonner/Radix) centers it, but sonner-native's toast
  // wrapper is a full-width `width:'100%'` View with default (stretch) alignment,
  // and a child with a definite (max-w-clamped) width can't stretch, so it pins
  // LEFT. `alignSelf:center` centers the capped card within that wrapper. Native
  // only — `native:` is a no-op on web where the engine already centers.
  // `select-none`: a toast is transient notification chrome, not a document —
  // dragging a selection across a card that vanishes in 4s is never intended.
  // Inherits to the title/description, so one class covers the whole card.
  // (Native RN `<Text>` is already non-selectable by default; this is the web
  // half.) Same call as the Badge chip.
  'relative flex flex-row select-none items-center gap-[8px] border native:self-center ' +
    'web:shadow-toast ' +
    // Figma "Glass: Effect(type: GLASS, radius: 4)". Applied to EVERY variant so
    // the translucent surfaces frost the content behind them (Figma tags only
    // the default node with the effect, but the colored fills are the most
    // translucent and read best with it — product decision). Web renders it as a
    // real CSS backdrop-filter, clipped to the card's rounded corners; native
    // paints an expo-blur BlurView (see ToastGlassBackground). Mirrors Button.
    'web:backdrop-blur-[6px] ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0',
  {
    variants: {
      variant: {
        default:
          `${TOAST_FILL_CLASS.default} border-secondary-200 ` +
          'web:focus-visible:ring-secondary-600',
        critical: `${TOAST_FILL_CLASS.critical} border-red-200 web:focus-visible:ring-red-800`,
        success: `${TOAST_FILL_CLASS.success} border-green-200 web:focus-visible:ring-green-800`,
        // The Professor-Ka conversational toast. Neutral surface — the SAME
        // `brand/secondary/130` (#f5f5f5b2) fill + `secondary-200` border as
        // `default`, verified against Figma nodes 5226:5313 / 5226:5493. Its
        // distinct shape (single content, chat-bubble radius, Ka) is
        // geometry, not colour — see the CONVERSATIONAL_* constants + Toast.tsx.
        conversational: `${TOAST_FILL_CLASS.conversational} border-secondary-200 web:focus-visible:ring-secondary-600`,
      },
      // Per-size geometry from Figma per-band captures. Heights/paddings are
      // exact; width is responsive (`w-full` + `max-w` cap at the Figma frame
      // width) so the card shrinks on narrow phones and stays overridable via
      // className. Radius uses the named preset tokens `rounded-toast-{sm,md}`.
      size: {
        // sm is a fixed 320px column (mobile always ships sm).
        // md HUGS its content between a 320px floor and a 560px cap (web always
        // ships md) so a one-line toast isn't a half-empty 400px slab NOR a tiny
        // sliver, and long copy still wraps instead of running edge to edge.
        // `w-fit` is a web-only concept; on native a View hugs its content by
        // default, which is the same intent, and the min/max caps apply on both.
        sm: 'px-[20px] py-[10px] rounded-toast-sm w-full max-w-[320px]',
        md: 'px-[22px] py-[12px] rounded-toast-md w-fit min-w-[320px] max-w-[560px]',
      },
      // A leading icon tightens the card's horizontal padding to a flat 16px at
      // both sizes — the icon carries its own optical inset, so the wider
      // text-only padding would read as a double gap.
      hasIcon: { true: '', false: '' },
    },
    compoundVariants: [
      { hasIcon: true, size: 'sm', class: 'px-[16px]' },
      { hasIcon: true, size: 'md', class: 'px-[16px]' },
    ],
    defaultVariants: { variant: 'default', size: 'sm', hasIcon: false },
  },
);

/**
 * Title typography + per-variant colour, via the shared named type tokens
 * (size + line-height, single source `typography.json`): sm → L2 (Bold 14/18),
 * md → H4 (SemiBold 16/24). Web variable Mona Sans respects
 * `font-bold`/`font-semibold`; native pins the per-weight .ttf family via
 * `native:font-mona-*` (no-op on web).
 *
 * LINE-HEIGHT IS TIGHTENED BY 2px against those tokens (L2 18→16, H4 24→22).
 * The Figma toast sets title and description with no gap between them, so the
 * stock line-heights left the two lines reading as separate blocks. Overridden
 * per-component rather than in `typography.json` — L2/H4/B2/B3 are shared by
 * the whole DS and only the toast wants them this tight.
 *
 * The override is in REM (remBase 16), matching how the type tokens are
 * emitted: `ResponsiveScaleProvider` rescales rem on native, so a px literal
 * would pin the line-height while the font size kept scaling and clip the text
 * on tablets / OS-large-text.
 */
export const toastTitleVariants = cva('text-left', {
  variants: {
    variant: {
      default: 'text-secondary-900 ',
      critical: 'text-red-900 ',
      success: 'text-green-900 ',
    },
    size: {
      // `leading-*` tightens the named token's line-height by 2px — see
      // TIGHTENED_LEADING. It must come AFTER `text-*`: the type token is a
      // Tailwind fontSize tuple carrying its own line-height, and Tailwind
      // emits the `lineHeight` core plugin after `fontSize`, so the explicit
      // utility wins (tailwind-merge keeps both, they're different groups).
      sm: 'text-l2 leading-[1rem] font-bold native:font-mona-bold',
      md: 'text-h4 leading-[1.375rem] font-semibold native:font-mona-semibold',
    },
  },
  defaultVariants: { variant: 'default', size: 'sm' },
});

/**
 * Description typography + per-variant colour, via the shared named type
 * tokens: sm → B3 (Medium 12/20), md → B2 (Medium 14/22) — each tightened by
 * 2px (B3 20→18, B2 22→20). Same reasoning + rem caveat as
 * {@link toastTitleVariants}.
 */
export const toastDescriptionVariants = cva('text-left', {
  variants: {
    variant: {
      default: 'text-secondary-600 ',
      critical: 'text-red-800 ',
      success: 'text-green-800 ',
    },
    size: {
      sm: 'text-b3 leading-[1.125rem] font-medium native:font-mona-medium',
      md: 'text-b2 leading-[1.25rem] font-medium native:font-mona-medium',
    },
  },
  defaultVariants: { variant: 'default', size: 'sm' },
});

/**
 * What a card on a NARROW window gets on top of its size: the row may wrap, and
 * it takes the full width of the box it was given.
 *
 * The row is [visual][copy][badge][action][✕] and everything in it except the
 * copy is `shrink-0`, so on a phone the copy was crushed to one word per line
 * beside a full-size button — and whatever still did not fit hung off the
 * screen. Wrapping drops the trailing controls to a second line instead and
 * leaves the copy a readable column (see {@link TOAST_COPY_BASIS} for what
 * decides when).
 *
 * `w-full` ships WITH it, and is not optional: `fit-content` on a wrapping flex
 * container is no longer the width of the row — the browser resolves it to
 * roughly the widest single item — so `w-fit` (the `md` size) collapses to a
 * card narrower than its own copy the moment wrapping is allowed. A definite
 * width keeps the wrap decision about the space the card actually has. A caller
 * that sets its own width still wins; `className` is merged last.
 *
 * `min-w-0` drops the `md` size's 320px FLOOR for the same reason: a min-width
 * outranks a max-width, so on a window narrower than the floor the card came
 * out wider than the box it was handed and hung off the screen edge. The floor
 * stays where it was written for — a desktop toast column — untouched.
 *
 * Applied from Toast.tsx against the WINDOW, not as a media query: NativeWind
 * would have to carry `max-md:` to native for that, and the mobile toast host
 * steps its own card width at exactly this breakpoint — so window is the number
 * both sides already agree on.
 */
export const TOAST_NARROW_CLASS = 'w-full min-w-0 flex-wrap';

/**
 * What a STACKED card gets on top of its size (see `ToastProps.layout`): the
 * anatomy turns into a column — visual, then copy, then controls — pinned to
 * the full width of the box the host gave it.
 *
 * This is the layout for a card whose action is a SENTENCE rather than a word
 * ("Follow 4 mates", "Obserwuj znajomego"): a button that wide cannot share a
 * 320pt row with a two-line headline at any wrap threshold, so the nudge does
 * not try. Reaching for it is a per-card DECISION, not a width measurement —
 * the same card stacks on a desktop toast column, where it has the room but
 * still reads better as one vertical block.
 *
 * `w-full min-w-0` for the same reason {@link TOAST_NARROW_CLASS} carries them:
 * it clears the `md` size's `w-fit` + 320px floor, which on a narrower host box
 * made the card wider than the box.
 */
export const TOAST_STACKED_CLASS = 'w-full min-w-0 flex-col items-start';

/**
 * The copy column's flex BASIS per size — the width it asks for before anything
 * shrinks, and therefore the width below which the trailing controls wrap to
 * their own line instead (see {@link TOAST_NARROW_CLASS}).
 *
 * Roughly 23 characters of the size's description type: enough that the copy
 * still reads as a sentence. Deliberately NOT a `min-width` — the column keeps
 * `min-w-0`, so on a card too narrow even for this it goes on shrinking (and
 * truncating) rather than overflowing.
 *
 * Chosen so the toasts we actually ship do NOT wrap: a 320pt phone toast with
 * a leading icon and an Undo action comes to ~270 of its 288 usable points.
 */
export const TOAST_COPY_BASIS = { sm: 'basis-[140px]', md: 'basis-[160px]' } as const;

/**
 * size → shared Button size for the action button (Figma: sm action is 36px =
 * Button `s`; md action is 40px = Button `m`).
 */
export const ACTION_BUTTON_SIZE = { sm: 's', md: 'm' } as const;

/**
 * variant → shared Button variant for the action button. The toast action's
 * fill/bevel anatomy is exactly a Button variant per Figma.
 */
export const ACTION_BUTTON_VARIANT = {
  default: 'secondary',
  critical: 'alert',
  success: 'success',
  // ponytail: the conversational toast never renders an action button (it is
  // content + optional badge only). `secondary` is an unused placeholder that
  // keeps this Record exhaustive over the variant union.
  conversational: 'secondary',
} as const;

/**
 * variant → shared {@link Badge} colour for the optional trailing badge slot.
 * Same mapping idea as {@link ACTION_BUTTON_VARIANT}: the badge inherits the
 * toast's semantic intent so call sites never pick a colour by hand.
 */
export const BADGE_COLOUR = {
  default: 'neutral',
  critical: 'error',
  success: 'success',
  // The conversational toast's badge carries a QUANTITY (MP, streak,
  // multiplier, credits) in the brand `primary` colour.
  conversational: 'primary',
} as const;

/**
 * size → shared Badge size. The badge sits inline with the title row, so it
 * stays a step below the action Button: sm → `xs` (24px), md → `s` (28px).
 *
 * The `conversational` variant overrides this — see {@link CONVERSATIONAL_BADGE_SIZE}.
 */
export const BADGE_SIZE = { sm: 'xs', md: 's' } as const;

/**
 * Badge size for the `conversational` toasts — `m` (32px), a step below the
 * legacy `l`, so the quantity chip reads without dominating the single line.
 */
export const CONVERSATIONAL_BADGE_SIZE = 's' as const;

/**
 * Ka leading-slot size for the conversational FALLBACK presentation, in
 * px (DS node 5856:49777). Exported because the mascot is injected BY THE
 * CALLER (`leadingSlot`) — mobile renders it at an explicit pixel size, so it
 * has to agree with the slot or the art gets cropped. It did: the caller was
 * still passing 32 after the slot moved to 24.
 */
export const CONVERSATIONAL_KA_SLOT_PX = 24;

/**
 * Toast card corner radius in px per size (Figma: sm 24, md 28) — mirror of the
 * `rounded-toast-{sm,md}` preset tokens. Used to clip the native glass BlurView
 * to the card shape (NativeWind can't hand a rem radius to an inline RN style).
 */
export const TOAST_RADIUS_PX = { sm: 24, md: 28 } as const;

/**
 * The card's border width in px — the base CVA's bare `border`. Not a rem
 * token, so it does NOT scale with the responsive rem. Used to inset the glass
 * clip radius off the outer radius above (see Toast.tsx).
 */
export const TOAST_BORDER_WIDTH = 1;

/**
 * The `conversational` variant owns its geometry instead of the sm/md sizes.
 * Two presentations, both read from Figma (file YZK9r3wDhvw1pY60KlVEIj):
 *
 *  - `bubble` (node 5226:5313) — the in-chat speech bubble out of the Ka
 *    launcher: `px-20 py-15`, all corners 24 EXCEPT bottom-right 8 (the corner
 *    that "points" down-right at the avatar). No leading slot.
 *  - `fallback` (node 5226:5493) — the system toast shown when the bubble can't
 *    (Ka absent / mini-chat open): symmetric radius 20, `pl-10 pr-20 py-10`,
 *    with the 32px Ka in the leading slot.
 *
 * Appended AFTER the CVA output in Toast.tsx so `twMerge` cleanly replaces the
 * size variant's padding / width / radius (each a single utility group).
 */
// Glass: conversational keeps the base's `web:backdrop-blur-[6px]`. The Figma
// conversational nodes (5226:5313 / 5226:5493) carry no glass effect, but the
// product decision is one glass treatment across ALL toasts — same call as the
// base CVA extending the default node's effect to success/critical.
export const CONVERSATIONAL_GEOMETRY = {
  // `rounded-br-none`: the tip corner is SQUARE (radius 0), not a small 8px
  // round — that hard corner is what points the bubble at the Ka launcher.
  bubble: 'w-fit max-w-[320px] px-[16px] py-[10px] rounded-[20px] rounded-br-none',
  fallback: 'w-fit max-w-[320px] px-[16px] py-[10px] rounded-[20px]',
} as const;

/**
 * Wider cap for the conversational bubble on NATIVE TABLETS only (iPad /
 * Android tablet — see the `usePlatformValue` call in Toast.tsx).
 *
 * Same reason the mobile system toast steps to 480 on tablet-width windows:
 * the DS rem scales type up to 1.25× at these widths (`computeResponsiveRem`),
 * so Figma's 320 cap wrapped one-line Ka lines into two or three with the rest
 * of the screen empty. Still `w-fit`, so short lines keep hugging.
 *
 * Deliberately NOT applied on web: there the bubble is anchored to the Ka
 * launcher and 320 is the Figma spec at every viewport.
 */
export const CONVERSATIONAL_TABLET_MAX_WIDTH = 'max-w-[480px]';

/**
 * Conversational card corner radii in px — mirror of {@link CONVERSATIONAL_GEOMETRY}'s
 * `rounded-*` classes, same job as {@link TOAST_RADIUS_PX}: clipping the native
 * glass BlurView. `bubbleTail` is the bubble's squared bottom-right corner.
 */
export const CONVERSATIONAL_RADIUS_PX = {
  bubble: 20,
  bubbleTail: 0,
  fallback: 20,
} as const;

/**
 * Single-line content typography for the `conversational` variant — Figma B2
 * (Mona Sans Medium 14/22), `secondary-900`. Bypasses the sm/md title matrix
 * (which is Bold/SemiBold): conversational content is one medium line, not a
 * title. Exported so web callers passing rich JSX (a `<Trans>`) can style it
 * to match the text-like path.
 */
export const CONVERSATIONAL_CONTENT_CLASS =
  'text-left text-l3 font-bold native:font-mona-bold text-secondary-600';

/**
 * Supporting line for a conversational toast that ships a `description` (the
 * reward/celebration copy: "Highlight tool unlocked" + "That's another Explore
 * Flashka task done."). The DS reference nodes render conversational content as
 * ONE wrapping L3 run, so there is no spec'd second line — this keeps the same
 * family and colour and separates the two by WEIGHT (bold headline, medium
 * support), with the toast's usual 2px-tighter leading.
 */
export const CONVERSATIONAL_DESCRIPTION_CLASS =
  'text-left text-b3 leading-[1.125rem] font-medium native:font-mona-medium text-secondary-600';

export type ToastVariantProps = VariantProps<typeof toastVariants>;
